import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import { ReflectionContextStep } from "@/components/ReflectionContextStep";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Flag } from "lucide-react";

const FLAG_REASONS = ["Unclear", "Hard to answer", "Doesn't apply to me", "Wording feels confusing", "Other"];

const SCALE = [
  { v: 1, label: "Strongly disagree" },
  { v: 2, label: "Disagree" },
  { v: 3, label: "Neither agree nor disagree" },
  { v: 4, label: "Agree" },
  { v: 5, label: "Strongly agree" },
];

const PRESSURE_EXAMPLES = [
  "disagreement with someone important to you",
  "family or relationship expectations",
  "workplace hierarchy or authority",
  "financial or career security",
  "uncertainty about an outcome",
  "expert advice",
  "obligations to other people",
  "fear of disappointing someone",
  "established expectations about what you \u201cshould\u201d do",
  "situations where productivity, achievement or approval matter",
  "decisions where your own needs, judgement or intuition conflict with other influences",
];

export default function Assessment() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState({});   // local selections {id: value}
  const [savedIds, setSavedIds] = useState(new Set()); // backend-confirmed item ids
  const [phase, setPhase] = useState("loading");     // loading | instructions | items
  const [ack, setAck] = useState(false);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [unanswered, setUnanswered] = useState([]);  // item ids still needing a response (backend)
  const pending = useRef({}); // id -> value not yet confirmed saved
  const itemShownAt = useRef(Date.now());
  const [flags, setFlags] = useState({});             // {itemId: {reason, comment}}
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagComment, setFlagComment] = useState("");
  const [missedMode, setMissedMode] = useState(false); // end-of-assessment "answer only missed items" flow

  const load = useCallback(async () => {
    try {
      let { data } = await api.get("/assessment/session/current");
      if (!data.session) {
        await api.post("/assessment/start");
        data = (await api.get("/assessment/session/current")).data;
      }
      setSession(data.session);
      setItems(data.items);
      setResponses(data.responses || {});
      setSavedIds(new Set(Object.keys(data.responses || {})));
      const firstUnanswered = data.items.findIndex((it) => !(it.id in (data.responses || {})));
      setIdx(firstUnanswered === -1 ? Math.max(0, data.items.length - 1) : firstUnanswered);
      const ctx = data.session.reflection_context;
      const org = !!(data.session.organization_id || data.session.campaign_id);
      const needsContext = !data.session.instructions_acknowledged && (!ctx || ((ctx.from_glimpse || org) && !data.session.context_seen));
      setPhase(needsContext ? "context" : data.session.instructions_acknowledged ? "items" : "instructions");
      try {
        const f = await api.get(`/pilot/flags/${data.session.id}`);
        setFlags(f.data.flags || {});
      } catch { /* non-blocking */ }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate(err.response?.status === 402 ? "/payment" : "/consent");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Reliable per-item save: each answer is saved on its own (no shared/cancelled timer).
  const saveOne = useCallback(async (sessionId, itemId, value, attempt = 0, ms = null) => {
    pending.current[itemId] = value;
    try {
      const body = { responses: { [itemId]: value } };
      if (ms != null) body.timings = { [itemId]: ms };
      await api.post(`/assessment/session/${sessionId}/responses`, body);
      if (pending.current[itemId] === value) delete pending.current[itemId];
      setSavedIds((s) => { const n = new Set(s); n.add(itemId); return n; });
    } catch {
      if (attempt < 3) setTimeout(() => saveOne(sessionId, itemId, value, attempt + 1, ms), 800 * (attempt + 1));
    }
  }, []);

  const answeredCount = savedIds.size;
  const total = items.length;
  const current = items[idx];
  const pct = total ? Math.round((answeredCount / total) * 100) : 0;
  const allAnswered = total > 0 && answeredCount >= total;

  // First index (in item order) whose id is in the given list of unanswered ids.
  const firstIdxForIds = (ids) => items.findIndex((it) => ids.includes(it.id));
  const enterMissedMode = (ids) => {
    setMissedMode(true);
    const t = firstIdxForIds(ids);
    if (t !== -1) setIdx(t);
  };
  // Move directly to the next still-unanswered item; if none remain, return to the completion screen.
  const advanceMissed = () => {
    const t = firstIdxForIds(unanswered);
    if (t !== -1) setIdx(t);
    else { setMissedMode(false); setIdx(total - 1); }
  };

  const choose = useCallback((value) => {
    if (!current || !session) return;
    const ms = Math.max(0, Date.now() - itemShownAt.current);
    setResponses((r) => ({ ...r, [current.id]: value }));
    saveOne(session.id, current.id, value, 0, ms);
    const remaining = unanswered.filter((x) => x !== current.id);
    setUnanswered(remaining);
    if (missedMode) {
      const nextIdx = items.findIndex((it) => remaining.includes(it.id));
      if (nextIdx !== -1) setTimeout(() => setIdx(nextIdx), 160);
      else { setMissedMode(false); setTimeout(() => setIdx(total - 1), 160); }
    } else if (idx < total - 1) {
      setTimeout(() => setIdx((i) => i + 1), 160);
    }
  }, [current, session, idx, total, saveOne, unanswered, missedMode, items]);

  useEffect(() => { itemShownAt.current = Date.now(); setFlagOpen(false); }, [idx]);

  const submitFlag = async (reason) => {
    if (!current || !session) return;
    try {
      await api.post("/pilot/flag-item", { session_id: session.id, item_id: current.id, reason, comment: flagComment || undefined });
      setFlags((f) => ({ ...f, [current.id]: { reason, comment: flagComment } }));
      setFlagOpen(false); setFlagComment("");
      toast.success("Thanks — this statement has been flagged for review");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeFlag = async () => {
    if (!current || !session) return;
    try {
      await api.delete(`/pilot/flag-item?session_id=${session.id}&item_id=${current.id}`);
      setFlags((f) => { const n = { ...f }; delete n[current.id]; return n; });
      setFlagOpen(false); setFlagComment("");
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (phase !== "items") return undefined;
    const onKey = (e) => {
      if (["1", "2", "3", "4", "5"].includes(e.key)) choose(Number(e.key));
      if (e.key === "ArrowLeft" && idx > 0) setIdx((i) => i - 1);
      if (e.key === "ArrowRight") { if (missedMode) advanceMissed(); else if (idx < total - 1) setIdx((i) => i + 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, choose, idx, total, missedMode, unanswered]);

  const saveContext = async (ctx) => {
    try {
      if (!session.reflection_context?.from_glimpse) {
        const { data } = await api.post(`/assessment/session/${session.id}/context`, ctx);
        setSession((s) => ({ ...s, reflection_context: data.reflection_context, context_seen: true }));
      } else setSession((s) => ({ ...s, context_seen: true }));
      setPhase(session.instructions_acknowledged ? "items" : "instructions");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const beginAssessment = async () => {
    try {
      await api.post(`/assessment/session/${session.id}/acknowledge-instructions`);
      setSession((s) => ({ ...s, instructions_acknowledged: true }));
      setPhase("items");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // 1) flush every local answer to the backend (covers any that failed mid-assessment)
      if (Object.keys(responses).length) {
        await api.post(`/assessment/session/${session.id}/responses`, { responses });
      }
      // 2) backend is authoritative for completion
      const { data: comp } = await api.get(`/assessment/session/${session.id}/completion`);
      setSavedIds(new Set(items.map((it) => it.id).filter((id) => !comp.unanswered_item_ids.includes(id))));
      if (!comp.complete) {
        setUnanswered(comp.unanswered_item_ids);
        enterMissedMode(comp.unanswered_item_ids);
        toast.error(`You're almost there. ${comp.unanswered_item_ids.length} statement${comp.unanswered_item_ids.length === 1 ? "" : "s"} still need a response.`);
        setSubmitting(false);
        return;
      }
      const { data } = await api.post(`/assessment/session/${session.id}/submit`);
      toast.success("Portrait generated");
      navigate(`/portrait/${data.session_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (phase === "loading") return (<div><HeaderNav /><div className="max-w-2xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading assessment…</div></div>);

  if (phase === "context") {
    const org = !!(session.organization_id || session.campaign_id);
    const initial = org ? (session.reflection_context || { category: "work" }) : session.reflection_context;
    return (<div><HeaderNav /><ReflectionContextStep key={`${session.id}-${session.reflection_context?.category || ""}`} initial={initial} locked={org || !!session.reflection_context?.from_glimpse} orgLocked={org} onSave={saveContext} /></div>);
  }

  if (phase === "instructions") {
    const isOrgSession = !!(session.organization_id || session.campaign_id);
    return (
      <div>
        <HeaderNav />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-12 pb-24 cg-fade-up" data-testid="before-you-begin">
          <p className="cg-eyebrow mb-3">Before you begin</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#F3F4F6] mb-6 leading-tight">Before you begin</h1>
          <div className="space-y-5 text-[#B9BDC7] leading-relaxed">
            <div className="space-y-3 border-l-2 border-brass/50 pl-4 text-[#E8EAEE]" data-testid="intro-purpose">
              <p>Your choices are shaped by many things, including people you trust, your experiences, your feelings and the technology you use.</p>
              <p>Getting advice or using AI does not mean giving up your authority.</p>
              <p>What matters is understanding how these influences affect your choices and deciding what role you want them to play.</p>
              <p>This assessment helps you explore those patterns. There are no right or wrong answers.</p>
            </div>
            {isOrgSession ? (
              <p data-testid="instruction-area">As you answer, think only about your experiences at work over the past <span className="text-[#F3F4F6]">6 to 12 months</span>. Consider how you make decisions, respond to pressure, and trust your own judgement in your work environment.</p>
            ) : (
              <p data-testid="instruction-area">Think about an area of your life where, over the past <span className="text-[#F3F4F6]">6 to 12 months</span>, you have found it difficult to make decisions or trust your own judgement. This could be at work, with family, in your relationships, or in your community. Keep this area in mind as you answer the questions.</p>
            )}
            <p>It can help to think about times when you felt pulled in different directions, such as:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              {PRESSURE_EXAMPLES.map((e) => <li key={e}>{e}</li>)}
            </ul>
            <div className="space-y-3 bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-5" data-testid={isOrgSession ? "intro-context-note-org" : "intro-context-note"}>
              {isOrgSession ? (
                <>
                  <p>If your experience changes depending on the situation, choose the answer that best describes how you usually feel or respond at work. Do not base your answers on experiences with family, friends, or other areas of your personal life.</p>
                  <p className="text-[#F3F4F6]">Remember, your answers reflect your experiences in your work environment. They do not define who you are in every situation.</p>
                </>
              ) : (
                <>
                  <p>If your experience changes depending on the situation, choose the answer that best describes how you usually feel or respond in the area of life you identified above.</p>
                  <p className="text-[#F3F4F6]">Remember, your answers reflect your experiences in this area of life. They do not define who you are in every situation.</p>
                </>
              )}
            </div>
            <p>Your Authority Portrait is private to you. If you're taking Caesar's Ghost through an organization or group, they will not see your answers or your report. They only see combined results once enough people have taken part to protect everyone's privacy.</p>
            <p>There are <span className="text-[#F3F4F6]">105 statements</span>. Most people take <span className="text-[#F3F4F6]">45 to 60 minutes</span>. Your progress is saved, so you can stop and come back later.</p>
          </div>
          <label className="flex items-start gap-3 mt-8 cursor-pointer" data-testid="instructions-ack-label">
            <input type="checkbox" data-testid="instructions-ack-checkbox" checked={ack}
              onChange={(e) => setAck(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[#D9A25F]" />
            <span className="text-[#E8EAEE]">I have read the instructions and understand how to approach the assessment.</span>
          </label>
          <button data-testid="begin-assessment-button" disabled={!ack} onClick={beginAssessment}
            className="mt-8 bg-brass text-obsidian font-medium px-7 py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40 disabled:cursor-not-allowed">
            Begin Assessment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderNav />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-10 pb-24">
        <div className="mb-10" data-testid="assessment-progress">
          <div className="flex justify-between items-baseline mb-2">
            <span className="cg-eyebrow">Item {Math.min(idx + 1, total)} of {total}</span>
            <span className="cg-eyebrow" data-testid="assessment-answered-count">{answeredCount} of {total} answered</span>
          </div>
          <div className="h-1 rounded-full bg-[#222634] overflow-hidden">
            <div className="h-full bg-brass rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {unanswered.length > 0 && (
          <div className="mb-8 border border-terracotta/50 bg-[rgba(198,93,59,0.08)] rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="unanswered-banner">
            <p className="text-sm text-[#E8EAEE]">You're almost there. <span className="text-terracotta">{unanswered.length}</span> statement{unanswered.length === 1 ? "" : "s"} still need a response.</p>
            <button data-testid="review-unanswered-button" onClick={() => enterMissedMode(unanswered)}
              className="text-sm text-brass border border-brass/50 px-4 py-1.5 rounded-full cg-scale-btn hover:bg-[rgba(217,162,95,0.1)]">
              Review unanswered statements
            </button>
          </div>
        )}

        {current && (
          <div key={current.id} className="cg-fade">
            <div className="flex items-center justify-between mb-6">
              <p className="cg-eyebrow">Answer for how you currently tend to make choices</p>
              {responses[current.id] !== undefined && (
                <span className="text-xs font-mono flex items-center gap-1" data-testid="autosave-indicator">
                  {savedIds.has(current.id)
                    ? <span className="text-[#7D9D86] flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>
                    : <span className="text-[#9CA3AF]">Saving…</span>}
                </span>
              )}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl leading-snug text-[#F3F4F6] mb-10 min-h-[4.5rem]"
                data-testid="assessment-item-text">
              {current.item_text}
            </h2>
            <div className="space-y-3" data-testid="assessment-scale">
              {SCALE.map((s) => {
                const selected = responses[current.id] === s.v;
                return (
                  <button key={s.v} data-testid={`scale-option-${s.v}`} onClick={() => choose(s.v)}
                    className={`cg-scale-btn w-full flex items-center gap-4 px-5 py-4 rounded-xl border text-left ${
                      selected ? "border-brass bg-[rgba(217,162,95,0.10)]" : "border-[var(--cg-border)] bg-[#1A1D27] hover:border-[rgba(217,162,95,0.4)]"
                    }`}>
                    <span className={`font-mono text-sm w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      selected ? "bg-brass text-obsidian" : "bg-[#222634] text-[#9CA3AF]"}`}>
                      {selected ? <Check className="w-3.5 h-3.5" /> : s.v}
                    </span>
                    <span className={selected ? "text-[#F3F4F6]" : "text-[#9CA3AF]"}>{s.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-7 flex items-center gap-3" data-testid="flag-row">
              <button data-testid="flag-statement-button" onClick={() => setFlagOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-brass cg-scale-btn">
                <Flag className={`w-3.5 h-3.5 ${flags[current.id] ? "text-brass" : ""}`} />
                {flags[current.id] ? "Statement flagged" : "Flag this statement"}
              </button>
            </div>
            {flagOpen && (
              <div className="mt-3 border border-[var(--cg-border)] bg-[#1A1D27] rounded-xl p-4" data-testid="flag-panel">
                <p className="text-xs text-[#9CA3AF] mb-3">Optional. This helps us improve Caesar's Ghost. It won't change your answer, your results, and it isn't shared with your organization.</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {FLAG_REASONS.map((r) => (
                    <button key={r} data-testid={`flag-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                      onClick={() => submitFlag(r)}
                      className="text-xs border border-[var(--cg-border)] text-[#B9BDC7] px-3 py-1.5 rounded-full hover:border-brass cg-scale-btn">{r}</button>
                  ))}
                </div>
                <textarea data-testid="flag-comment" value={flagComment} onChange={(e) => setFlagComment(e.target.value)}
                  placeholder="Optional: add a short comment" rows={2}
                  className="w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#E8EAEE] focus:border-brass outline-none" />
                {flags[current.id] && (
                  <button data-testid="flag-remove" onClick={removeFlag}
                    className="mt-3 text-xs text-[#9CA3AF] hover:text-terracotta">Remove flag</button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <button data-testid="assessment-prev-button" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)}
            className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass disabled:opacity-30 cg-scale-btn">
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
          <span className="text-xs text-[#6B7280] font-mono hidden sm:block">Tip: press 1–5 to answer</span>
          {missedMode ? (
            <button data-testid="assessment-next-button" onClick={advanceMissed}
              className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass cg-scale-btn">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : idx < total - 1 ? (
            <button data-testid="assessment-next-button" onClick={() => setIdx((i) => i + 1)}
              className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass cg-scale-btn">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="assessment-submit-button" disabled={submitting} onClick={submit}
              className="inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-6 py-2.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40">
              {submitting ? "Checking…" : "Generate Portrait"}
            </button>
          )}
        </div>

        {idx === total - 1 && (
          <p className="mt-6 text-sm text-[#9CA3AF] leading-relaxed" data-testid="after-submit-note">
            When you finish, we create your Authority Portrait. It shows your seven dimensions and explains what your answers suggest. It also gives you questions to think about and small things you can try. You can come back to it at any time and download a PDF copy.
          </p>
        )}
        {!allAnswered && idx === total - 1 && unanswered.length === 0 && (
          <p className="mt-4 text-sm text-[#9CA3AF] text-right" data-testid="assessment-incomplete-note">
            {answeredCount} of {total} answered. Select "Generate Portrait" and we'll take you to anything still unanswered.
          </p>
        )}
      </div>
    </div>
  );
}
